import { Box, Grid, useDisclosure } from '@chakra-ui/react'
import type { Offer } from 'BrokerTsLib/src/dtos'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { CustomWaypoint } from '@/components/CustomWaypoint'
import { InsuranceQuoteGroup } from '@/components/InsuranceQuoteGroup'
import { Parameters } from '@/components/Parameters/Parameters'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useOffersTravelContext } from '@/context/OffersTravelProvider'
import { useQuotes } from '@/context/QuotesContext'
import { useSidebar } from '@/context/SidebarContext'
import type { PageSection as Section } from '@/layouts/components/Sidebar'
import { useScrollToPageSection } from '@/layouts/components/Sidebar/Sidebar'

import { OfferPackageGrid } from '../../offers/grid/package/OfferPackageGrid'

export const Step2SidebarIds = {
  Parameters: 'parameters',
  Quotes: 'quotes',
} as const

export type Step2SidebarIds = typeof Step2SidebarIds[keyof typeof Step2SidebarIds]

export const Step2 = () => {
  const quotesRef = useRef(null)

  const { pageSections, setPageSections } = useSidebar()
  const scrollToPageSection = useScrollToPageSection()
  const { insuranceParams, refetchInsuranceParams, isDisabled } =
    useInsuranceParams()

  const {
    isOpen: isOpenParameters,
    onOpen: onOpenParameters,
    onToggle: onToggleParameters,
  } = useDisclosure({
    defaultIsOpen: !insuranceParams?.generalParams.existsOffers,
  })
  const {
    refetchOffers: getOffers,
    offers,
    showAll,
    setShowAll: changeShowAll,
    filtered,
    isLoading,
    calculatingOffersIds,
  } = useOffersTravelContext()

  const {
    setShowFilter,
    onCalculationStateChange,
    calculationState,
    setCalculationState,
  } = useQuotes()
  const { setWrapperMarginTop } = useSidebar()
  const [highlightedQuotesList, setHighlightedQuotesList] = useState<Offer[]>(
    [],
  )
  const [ratingChanged, setRatingChanged] = useState(new Date())
  const [shouldScrollToQuotes, setShouldScrollToQuotes] = useState(false)

  const isCalculatingAny = isLoading || (calculatingOffersIds?.length ?? 0) > 0

  const handleState = useCallback(
    (isCalculated: boolean, isCalculating: boolean) => {
      setShowFilter(isCalculated)

      setCalculationState({
        isCalculated,
        isCalculating,
      })
      onCalculationStateChange?.({
        isCalculated,
        isCalculating,
      })
    },
    [],
  )

  // ziskani offers na page load, pokud nejake existuji
  const [firstLoad, setFirstLoad] = useState(true)
  useEffect(() => {
    if (firstLoad) setFirstLoad(false)
    else return

    // offery ziskavam na page load pouze pokud existuji nejake
    if (!insuranceParams?.generalParams.existsOffers) return

    handleState(false, true)

    setTimeout(() => {
      scrollToQuotes()
    }, 1000)

    getOffers({})
  }, [insuranceParams?.generalParams.existsOffers, firstLoad])

  // pri scrollu na offers jenom nastavim priznak a o samotny scroll se postara useEffect
  const scrollToQuotes = () => {
    setShouldScrollToQuotes(true)
  }

  // scrollovani na kalkulace
  useEffect(() => {
    // Chceme scroll až po zobrazení sekce nabídek
    if (
      shouldScrollToQuotes &&
      insuranceParams?.generalParams.existsOffers &&
      (calculationState.isCalculating ||
        (calculationState.isCalculated && filtered.length == 0))
    )
      scrollToPageSection(Step2SidebarIds.Quotes)
  }, [
    shouldScrollToQuotes,
    insuranceParams?.generalParams.existsOffers,
    calculationState.isCalculating,
    calculationState.isCalculated,
    filtered.length,
  ])

  // automaticka zmena stavu na zaklade dat
  useEffect(() => {
    // do offers se dostane az po kalkulaci, nebo kdyz je nabidka uz sjednana
    handleState(offers.length > 0, isCalculatingAny)
  }, [offers.length, isCalculatingAny])

  // ziskanni / zajisteni vytvoreni offers
  const handleCalculateOffers = () =>
    getOffers({ force: true }).then(() => {
      setShowFilter(true)

      // aby se pripadne nove varianty zobrazily ve filteru a aby paramsy uz na sobe mely existsOffers
      if (!insuranceParams?.generalParams.existsOffers) refetchInsuranceParams()
    })

  const handleAddNewVariant = () => {
    onOpenParameters()
    scrollToPageSection(Step2SidebarIds.Parameters)
  }

  useEffect(() => {
    const newHightlightedQuotes = filtered.filter(
      (quote) =>
        quote.stars! > 0 ||
        quote.offerId == insuranceParams?.generalParams.selectedOfferId,
    )

    setHighlightedQuotesList(newHightlightedQuotes)
  }, [filtered, ratingChanged])

  useEffect(() => {
    if (calculationState.isCalculated) {
      const quotesSection = document.getElementById(Step2SidebarIds.Quotes)
      const marginTop = quotesSection?.getBoundingClientRect().top
      if (marginTop)
        setWrapperMarginTop?.(marginTop)
    }
  }, [calculationState.isCalculated, quotesRef, setWrapperMarginTop])

  const variants = insuranceParams?.baseParams?.variants ?? []
  // registrace sekcí
  useEffect(() => {
    const array: Section[] = []
    let i = 1

    array.push({
      id: Step2SidebarIds.Parameters,
      sectionTitle: 'Parametry pojištění',
      index: i++,
      children: (
        <CustomWaypoint sectionId={Step2SidebarIds.Parameters}>
          <Box>
            <Parameters
              isCollapsed={!isOpenParameters}
              id={Step2SidebarIds.Parameters}
              cardHeaderProps={{
                cursor: 'pointer',
                onClick: onToggleParameters,
              }}
              calculationState={calculationState}
              onCalculateOffers={handleCalculateOffers}
            />
          </Box>
        </CustomWaypoint>
      ),
    })

    if (insuranceParams?.generalParams.existsOffers)
      array.push({
        id: Step2SidebarIds.Quotes,
        sectionTitle: calculationState.isCalculating
          ? 'Kalkuluji...'
          : 'Kalkulace pojištění',
        isDisabled: !calculationState.isCalculated || isDisabled,
        index: i++,
        children: (
          <CustomWaypoint sectionId={Step2SidebarIds.Quotes}>
            <Grid sx={{ gap: 8 }}>
              <InsuranceQuoteGroup
                id={Step2SidebarIds.Quotes}
                ref={quotesRef}
                groupHeaderProps={{
                  visibleCount: filtered.length,
                  totalCount: offers.length,
                  enableAddVariant: !isDisabled,
                  sectionTitle: calculationState.isCalculating
                    ? 'Kalkuluji...'
                    : 'Kalkulace pojištění',
                  onShowAllQuotes: () => changeShowAll(!showAll),
                  calculationState: calculationState,
                  onAddNewVariant: () => {
                    handleAddNewVariant()
                  },
                }}
                groupFooterProps={{
                  enableAddVariant: !isDisabled,
                  onAddNewVariant: () => {
                    handleAddNewVariant()
                  },
                }}
              >
                {calculationState.isCalculated && <OfferPackageGrid variants={variants} icon={''} />}
              </InsuranceQuoteGroup>
            </Grid>
          </CustomWaypoint>
        ),
      })

    setPageSections(array)
  }, [
    calculationState.isCalculating,
    calculationState.isCalculated,
    filtered,
    insuranceParams?.generalParams.existsOffers,
    isOpenParameters,
  ])

  return (
    <Grid sx={{ gap: 8 }}>
      {pageSections.map((x) => (
        <React.Fragment key={x.id}>{x.children}</React.Fragment>
      ))}
    </Grid>
  )
}
